/**
 * VÉRAULT Portal — Advisor: Trash (Wave 26.1)
 *
 * Lists soft-deleted Trips + Clients, with restore + permanent purge actions.
 * Records are auto-purged after 30 days by `POST /api/travels/trash/purge-expired`
 * (advisor-triggered today; can be moved to a scheduled job later).
 */
import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Trash2, RotateCcw, AlertCircle, Loader2 } from "lucide-react";
import {
  Surface,
  Heading,
  PageHeader,
  EditorialRule,
  StatusTag,
  PortalButton,
  PortalBreadcrumb,
  Empty,
  Skeleton,
  api,
  fmt,
  motion as portalMotion,
} from "../../index";

const { fadeInUp, stagger } = portalMotion;

function daysLeftTone(days) {
  if (days <= 3) return "terracotta";
  if (days <= 10) return "gold";
  return "neutral";
}

export default function AdvisorTrash() {
  const [data, setData] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [busyId, setBusyId] = React.useState(null);
  const [purgeBusy, setPurgeBusy] = React.useState(false);

  const load = React.useCallback(() => {
    setError(null);
    api
      .get("/travels/trash")
      .then(({ data }) => setData(data))
      .catch(() => setError("Couldn't load Trash."));
  }, []);

  React.useEffect(() => {
    load();
  }, [load]);

  const restoreTrip = async (id) => {
    setBusyId(`trip:${id}`);
    try {
      await api.post(`/travels/trips/${id}/restore`);
      load();
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Restore failed.");
    } finally {
      setBusyId(null);
    }
  };
  const purgeTrip = async (id, label) => {
    if (!window.confirm(`Permanently delete "${label || id}" and its itinerary, recommendations, and change requests?\n\nThis cannot be undone.`)) return;
    setBusyId(`trip:${id}`);
    try {
      await api.delete(`/travels/trips/${id}?hard=true`);
      load();
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Purge failed.");
    } finally {
      setBusyId(null);
    }
  };
  const restoreClient = async (id) => {
    setBusyId(`client:${id}`);
    try {
      await api.post(`/travels/clients/${id}/restore`);
      load();
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Restore failed.");
    } finally {
      setBusyId(null);
    }
  };
  const purgeClient = async (id, label) => {
    if (!window.confirm(`Permanently delete client "${label || id}"?\n\nThis cannot be undone. Their trips remain in Trash separately.`)) return;
    setBusyId(`client:${id}`);
    try {
      await api.delete(`/travels/clients/${id}?hard=true`);
      load();
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Purge failed.");
    } finally {
      setBusyId(null);
    }
  };

  const purgeAllExpired = async () => {
    if (!window.confirm("Permanently purge ALL items older than 30 days from Trash?\n\nThis cannot be undone.")) return;
    setPurgeBusy(true);
    try {
      const { data: res } = await api.post("/travels/trash/purge-expired");
      window.alert(`Purged ${res.purged_trips} trip(s) and ${res.purged_clients} client(s).`);
      load();
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Purge failed.");
    } finally {
      setPurgeBusy(false);
    }
  };

  return (
    <motion.div initial="initial" animate="animate" variants={stagger} className="space-y-10" data-testid="trash-page">
      <motion.div variants={fadeInUp}>
        <PortalBreadcrumb
          parent={{ to: "/portal/advisor/settings", label: "Settings" }}
          trail={[{ label: "Trash" }]}
        />
        <PageHeader
          eyebrow="Settings · Trash"
          title="Recently deleted"
          lede="Soft-deleted experiences and clients live here for 30 days, then are permanently purged."
          actions={
            <PortalButton
              variant="ghost"
              size="sm"
              icon={<Trash2 className="size-3.5" strokeWidth={1.5} />}
              onClick={purgeAllExpired}
              disabled={purgeBusy}
              data-testid="trash-purge-expired"
              title="Permanently purge anything older than 30 days"
            >
              {purgeBusy ? "Purging…" : "Purge expired"}
            </PortalButton>
          }
        />
      </motion.div>

      {error && (
        <Empty title={error} icon={<AlertCircle className="size-7" strokeWidth={1.25} />} />
      )}

      {!data && !error && (
        <div className="space-y-3">
          <Skeleton className="h-16" />
          <Skeleton className="h-16" />
        </div>
      )}

      {data && (
        <>
          {/* Trips */}
          <motion.section variants={fadeInUp} data-testid="trash-trips">
            <EditorialRule label={`Experiences · ${data.trips.length}`} />
            <div className="mt-5">
              {data.trips.length === 0 ? (
                <Empty title="No deleted experiences" icon={<Trash2 className="size-6" strokeWidth={1.25} />} />
              ) : (
                <Surface variant="default" padded={false} className="overflow-hidden">
                  <ul className="divide-y divide-[rgba(0,0,0,0.05)]">
                    {data.trips.map((t) => {
                      const label = t.internal_trip_name || t.client_trip_title || t.destinations || "Untitled experience";
                      const isBusy = busyId === `trip:${t.id}`;
                      return (
                        <li
                          key={t.id}
                          className="flex items-center justify-between gap-4 px-5 sm:px-6 py-4"
                          data-testid={`trash-trip-${t.id}`}
                        >
                          <div className="min-w-0 flex-1">
                            <p className="font-serif text-[16px] text-[hsl(var(--foreground))] truncate">{label}</p>
                            <p className="mt-0.5 font-body text-[12.5px] text-[hsl(var(--foreground-secondary))] truncate">
                              {[t.client_name, t.destinations].filter(Boolean).join(" · ") || "—"}
                            </p>
                            <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))]">
                              Deleted {fmt.formatDate(t.deleted_at)}
                            </p>
                          </div>
                          <StatusTag tone={daysLeftTone(t.days_left_until_purge)}>
                            {t.days_left_until_purge}d left
                          </StatusTag>
                          <div className="flex items-center gap-1.5 shrink-0">
                            <PortalButton
                              variant="ghost"
                              size="xs"
                              icon={isBusy ? <Loader2 className="size-3.5 animate-spin" /> : <RotateCcw className="size-3.5" strokeWidth={1.5} />}
                              disabled={isBusy}
                              onClick={() => restoreTrip(t.id)}
                              data-testid={`trash-trip-${t.id}-restore`}
                            >
                              Restore
                            </PortalButton>
                            <PortalButton
                              variant="ghost"
                              size="xs"
                              icon={<Trash2 className="size-3.5" strokeWidth={1.5} />}
                              disabled={isBusy}
                              onClick={() => purgeTrip(t.id, label)}
                              data-testid={`trash-trip-${t.id}-purge`}
                            >
                              Purge
                            </PortalButton>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </Surface>
              )}
            </div>
          </motion.section>

          {/* Clients */}
          <motion.section variants={fadeInUp} data-testid="trash-clients">
            <EditorialRule label={`Clients · ${data.clients.length}`} />
            <div className="mt-5">
              {data.clients.length === 0 ? (
                <Empty title="No deleted clients" icon={<Trash2 className="size-6" strokeWidth={1.25} />} />
              ) : (
                <Surface variant="default" padded={false} className="overflow-hidden">
                  <ul className="divide-y divide-[rgba(0,0,0,0.05)]">
                    {data.clients.map((c) => {
                      const label = c.full_name || "Unnamed client";
                      const isBusy = busyId === `client:${c.id}`;
                      return (
                        <li
                          key={c.id}
                          className="flex items-center justify-between gap-4 px-5 sm:px-6 py-4"
                          data-testid={`trash-client-${c.id}`}
                        >
                          <div className="min-w-0 flex-1">
                            <p className="font-serif text-[16px] text-[hsl(var(--foreground))] truncate">{label}</p>
                            <p className="mt-0.5 font-body text-[12.5px] text-[hsl(var(--foreground-secondary))] truncate">
                              {[c.email, c.phone].filter(Boolean).join(" · ") || "—"}
                            </p>
                            <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--foreground-muted))]">
                              Deleted {fmt.formatDate(c.deleted_at)}
                            </p>
                          </div>
                          <StatusTag tone={daysLeftTone(c.days_left_until_purge)}>
                            {c.days_left_until_purge}d left
                          </StatusTag>
                          <div className="flex items-center gap-1.5 shrink-0">
                            <PortalButton
                              variant="ghost"
                              size="xs"
                              icon={isBusy ? <Loader2 className="size-3.5 animate-spin" /> : <RotateCcw className="size-3.5" strokeWidth={1.5} />}
                              disabled={isBusy}
                              onClick={() => restoreClient(c.id)}
                              data-testid={`trash-client-${c.id}-restore`}
                            >
                              Restore
                            </PortalButton>
                            <PortalButton
                              variant="ghost"
                              size="xs"
                              icon={<Trash2 className="size-3.5" strokeWidth={1.5} />}
                              disabled={isBusy}
                              onClick={() => purgeClient(c.id, label)}
                              data-testid={`trash-client-${c.id}-purge`}
                            >
                              Purge
                            </PortalButton>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </Surface>
              )}
            </div>
          </motion.section>
        </>
      )}
    </motion.div>
  );
}
